import { createTheme } from '@mui/material/styles';

// Color palette from ctrnd.com - Dark, low-key theme
const colors = {
  primary: {
    main: '#8B1F2B',      // Dark wine red (derived from logo #F5333F)
    light: '#A6252F',     // Lighter wine for hover
    dark: '#6B1821',      // Darker wine for active states
  },
  secondary: {
    main: '#414042',      // Dark gray from logo
    light: '#6D6E71',     // Medium gray from logo
    dark: '#2a2a2b',      // Darker for active
  },
  error: {
    main: '#F5333F',      // Bright red from logo for errors
    light: '#f75961',     // Lighter red
    dark: '#d12835',      // Darker red
  },
  warning: {
    main: '#ff9800',      // Orange for warnings
    light: '#ffb74d',     // Lighter orange
    dark: '#f57c00',      // Darker orange
  },
  text: {
    primary: '#E5E5E5',   // Light gray for headings (dark theme)
    secondary: '#B0B0B0', // Medium gray for body text
    disabled: '#707070',  // Darker gray for disabled
  },
  background: {
    default: '#1a1a1b',   // Very dark background
    paper: '#242424',     // Slightly lighter for cards
    subtle: '#2d2d2e',    // Subtle backgrounds for headers
  },
  divider: '#3a3a3a',     // Dark dividers
  border: {
    main: '#4a4a4a',      // Form field borders
    light: '#3a3a3a',     // Light borders
  },
  grey: {
    100: '#2d2d2e',
    200: '#3a3a3a',
    300: '#4a4a4a',
    400: '#5a5a5a',
    500: '#707070',
    600: '#939598',       // From logo
    700: '#A7A9AC',       // From logo
  },
  // Wine red variations for alternating table rows (2 colors with better contrast)
  tableRows: {
    row1: '#422e30',      // Darker desaturated wine
    row2: '#3d2224',      // Lighter desaturated wine with more contrast
  },
};

// Create the custom theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: '#ffffff',
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
      contrastText: '#ffffff',
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
      contrastText: '#000000',
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    divider: colors.divider,
    grey: colors.grey,
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontSize: 13, // Smaller base font size for high-density
    h1: {
      color: colors.text.primary,
      fontWeight: 500,
      fontSize: '1.75rem',
    },
    h2: {
      color: colors.text.primary,
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    h3: {
      color: colors.text.primary,
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h4: {
      color: colors.text.primary,
      fontWeight: 500,
      fontSize: '1.1rem',
    },
    h5: {
      color: colors.text.primary,
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    h6: {
      color: colors.text.primary,
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    body1: {
      color: colors.text.secondary,
      fontSize: '0.875rem',
    },
    body2: {
      color: colors.text.secondary,
      fontSize: '0.8125rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
        sizeSmall: {
          padding: '2px 8px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(139, 31, 43, 0.4)', // Wine red glow
          },
        },
        outlined: {
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: 'rgba(139, 31, 43, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundImage: 'none', // Remove default MUI gradient
        },
        outlined: {
          borderColor: colors.border.light,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.divider}`,
          padding: '4px 8px',
          fontSize: '0.8125rem',
          color: colors.text.secondary,
        },
        head: {
          backgroundColor: '#6b4747',
          color: colors.text.primary,
          fontWeight: 600,
          padding: '4px 8px', // Reduced by 30% from 6px
          fontSize: '0.8125rem',
          borderBottom: `2px solid ${colors.primary.dark}`,
        },
        sizeSmall: {
          padding: '2px 6px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // Alternating row colors using 2 wine red variations with better contrast
          '&:nth-of-type(even)': {
            backgroundColor: colors.tableRows.row1,
          },
          '&:nth-of-type(odd)': {
            backgroundColor: colors.tableRows.row2,
          },
          '&:hover': {
            backgroundColor: `${colors.primary.dark}40`, // Wine red with transparency on hover
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}60`,
            '&:hover': {
              backgroundColor: `${colors.primary.main}80`,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.background.subtle,
            '& fieldset': {
              borderColor: colors.border.main,
            },
            '&:hover fieldset': {
              borderColor: colors.primary.light,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-input': {
            color: colors.text.primary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.subtle,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          '&:hover': {
            backgroundColor: `${colors.primary.dark}40`,
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}30`,
            '&:hover': {
              backgroundColor: `${colors.primary.main}50`,
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.divider}`,
          minHeight: '36px', // Compact tabs
        },
        indicator: {
          backgroundColor: colors.primary.main,
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          minHeight: '36px',
          padding: '6px 12px',
          color: colors.text.secondary,
          '&.Mui-selected': {
            color: colors.primary.light,
          },
          '&:hover': {
            color: colors.primary.light,
            backgroundColor: `${colors.primary.dark}20`,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

export default theme;
